// Enhanced structured logger with context support
// - info/warn/error: always visible
// - debug/log: silenced in production
// - performance: track timing and metrics

const isProd: boolean =
  typeof import.meta !== 'undefined' && !!import.meta.env && !!import.meta.env.PROD;

type LogContext = Record<string, unknown>;
type Reporter = (e: unknown, context?: LogContext) => void;

let errorReporter: Reporter | null = null;
let defaultContext: LogContext = {};

export function setErrorReporter(r: Reporter) {
  errorReporter = r;
}

export function setDefaultContext(ctx: LogContext) {
  defaultContext = { ...defaultContext, ...ctx };
}

function formatMessage(level: string, args: unknown[], context?: LogContext): void {
  const timestamp = new Date().toISOString();
  const ctx = { ...defaultContext, ...context };
  const hasContext = Object.keys(ctx).length > 0;
  
  if (hasContext && !isProd) {
    console.groupCollapsed(`[VERAIS:${level}]`, timestamp, ...args);
    console.table(ctx);
    console.groupEnd();
  }
}

export function info(...args: unknown[]): void;
export function info(context: LogContext, ...args: unknown[]): void;
export function info(...args: unknown[]): void {
  const [first, ...rest] = args;
  const context = typeof first === 'object' && first !== null && !Array.isArray(first) ? first as LogContext : undefined;
  const message = context ? rest : args;
  console.info('[VERAIS]', ...message);
  formatMessage('INFO', message, context);
}

export function warn(...args: unknown[]): void;
export function warn(context: LogContext, ...args: unknown[]): void;
export function warn(...args: unknown[]): void {
  const [first, ...rest] = args;
  const context = typeof first === 'object' && first !== null && !Array.isArray(first) ? first as LogContext : undefined;
  const message = context ? rest : args;
  console.warn('[VERAIS]', ...message);
  formatMessage('WARN', message, context);
}

export function error(...args: unknown[]): void;
export function error(context: LogContext, ...args: unknown[]): void;
export function error(...args: unknown[]): void {
  const [first, ...rest] = args;
  const context = typeof first === 'object' && first !== null && !Array.isArray(first) ? first as LogContext : undefined;
  const message = context ? rest : args;
  const errorArg = context ? rest[0] : first;
  
  console.error('[VERAIS]', ...message);
  formatMessage('ERROR', message, context);
  
  if (errorReporter) {
    const err = errorArg instanceof Error ? errorArg : new Error(String(errorArg));
    try {
      errorReporter(err, context);
    } catch {
      /* no-op */
    }
  }
}

export function debug(...args: unknown[]): void {
  if (isProd) return;
  console.debug('[VERAIS]', ...args);
}

export function log(...args: unknown[]): void {
  if (isProd) return;
  console.log('[VERAIS]', ...args);
}

export function performance(label: string, fn: () => void | Promise<void>): void {
  if (isProd) return;
  const start = Date.now();
  const result = fn();
  
  if (result instanceof Promise) {
    result.finally(() => {
      const duration = Date.now() - start;
      console.log(`[VERAIS:PERF] ${label}: ${duration}ms`);
    });
  } else {
    const duration = Date.now() - start;
    console.log(`[VERAIS:PERF] ${label}: ${duration}ms`);
  }
}

export const logger = { 
  info, 
  warn, 
  error, 
  debug, 
  log, 
  performance,
  setErrorReporter,
  setDefaultContext
} as const;

export type Logger = typeof logger;

export default logger;
